/**
 * The address a picture URN points at, or null when it names nothing this
 * client can draw.
 *
 * A URN carries either inline SVG markup (`urn:svg:<svg .../>`), a link
 * (`urn:link:/assets/logo.png`) or a plain http(s) address. It is what a
 * picture component reads its image from, and what a sidebar item carries when
 * it is drawn with an icon of its own instead of a built-in glyph.
 */
export function resolvePictureUrn(value: string): string | null {
  const normalized = value.trim();

  if (normalized.startsWith("urn:svg:")) {
    return resolveInlineSvgUrn(normalized.slice("urn:svg:".length));
  }

  if (normalized.startsWith("urn:link:")) {
    return resolveLinkUrn(normalized.slice("urn:link:".length).trim());
  }

  // A picture also takes a plain web address, which is what a `context://`
  // reference resolves to when the record carries a picture of its own. Only
  // an absolute http(s) address is read this way: a reference that resolved
  // to nothing leaves its own text behind, and that must draw the
  // placeholder rather than become a request.
  return /^https?:\/\//i.test(normalized) ? resolveLinkUrn(normalized) : null;
}

function resolveLinkUrn(target: string): string | null {
  if (!target) return null;

  try {
    return new URL(target, window.location.origin).toString();
  } catch {
    return null;
  }
}

// Inline SVG markup travels as a data URI on the same `<img>` the linked picture
// uses. The browser renders such an image in its own isolated context, so scripts
// and external references inside the markup never run against this document.
function resolveInlineSvgUrn(markup: string): string | null {
  const svg = markup.trim();
  if (!svg.toLowerCase().startsWith("<svg")) return null;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
